import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Bundle, BundleOrderGroupForCreation, BundleOrderGroupsRepositoryService, BundlesRepositoryService, ConfigurationState, ConfirmDialogComponent, ListComponent, Order, OrderListItem, OrderRepositoryService } from 'reg-hub-common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { OrderListRepositoryService } from '../../services/order-list/order-list-repository.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-search-orders',
    templateUrl: './search-orders.component.html',
    styleUrls: ['./search-orders.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchOrdersComponent extends ListComponent<OrderListItem> implements OnInit {
  private readonly transloco = inject(TranslocoService);

  protected _expandedOrder: OrderListItem | null = null;
  protected _loadedOrders: Map<string, Order> = new Map();
  protected _loadingOrderId: string | null = null;
  protected bundle: Bundle | null = null;

  displayedColumns: string[] = [
    'referenceNumber',
    'orderTypeID',
    'orderStatusTypeID',
    'country',
    'jurisdiction',
    'dateRequested'
  ];

  columnsToDisplayWithView = [...this.displayedColumns, 'view', 'indicator'];

  constructor(
    repo: OrderListRepositoryService,
    private orderRepo: OrderRepositoryService,
    private activatedRoute: ActivatedRoute,
    private configurationState: ConfigurationState,
    private bundlesRepository: BundlesRepositoryService,
    private bundleOrdersRepo: BundleOrderGroupsRepositoryService,
    private dialog: MatDialog,
    private messageService: ToastrService,
    private router: Router) {
    super(repo);

  }

  ngOnInit() {
    let bundleID = this.activatedRoute.snapshot.queryParams['targetBundleID'];
    if (bundleID) {
      this.configurationState.getCorporationID().then(corporationID => {
        this.bundlesRepository.populateBaseLink([corporationID])
        this.bundlesRepository.getBundle(bundleID).subscribe(bundle => {
          this.bundle = bundle;
        })
      });
    }
  }

  protected toggleExpand(order: OrderListItem) {
    if (this._expandedOrder === order) {
      this._expandedOrder = null;
      return;
    }

    this._expandedOrder = order;

    if (!this._loadedOrders.has(order.id)) {
      this._loadingOrderId = order.id;
      this.orderRepo.getOrder('Orders', order.id).subscribe(fullOrder => {
        this._loadedOrders.set(order.id, fullOrder);
        this._loadingOrderId = null;
      });
    }
  }

  protected getFullOrder(orderId: string): Order | null {
    return this._loadedOrders.get(orderId) ?? null;
  }

  protected viewOrder(order: OrderListItem, newTab: boolean) {
    if (newTab) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['orders', order.id])
      );

      window.open(url, '_blank');
    }
    else {
      this.router.navigate(['orders', order.id])
    }
  }

  getTitle() {
    return this.transloco.translate(this.bundle ? 'orders.addOrdersToBundle' : 'navigation.menu.orderHistory');
  }

  addToBundleConfirmation() {
    if (this.bundle) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.transloco.translate('orders.addToBundle2'),
          confirmationPrompt: this.transloco.translate('orders.confirmAddTheCurrentPageOf', { bundleName: this.bundle.name }),
          positiveResponseLabel: this.transloco.translate('common.buttons.confirm'),
          negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
        },
        minWidth: '800px'
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // submission is confirmed
          this.addToBundle();
        }
      });
    }
  }

  addToBundle() {
      // Bundle operations need the full Order (for orderGroup), so use OrderRepositoryService
      this.orderRepo.getOrders({
        isVisible: true,
        pageSize: 100
      }).subscribe(orders => {
        let newBundles: BundleOrderGroupForCreation[] = [];
        orders.forEach(order => {
          if (!order.orderGroup?.bundles?.find(bundle => bundle.id == this.bundle!.id)) {
            let newBundleOrder: BundleOrderGroupForCreation = {
              orderGroupID: order.orderGroup!.id
            };
            newBundles = newBundles.concat(newBundleOrder);
          }
        });

        this.bundleOrdersRepo.createBundleOrderGroups(this.bundle!.corporationID, this.bundle!.id, newBundles)
          .subscribe({
            next: _ => {
              if (newBundles.length < orders.length) {
                this.messageService.success(this.transloco.translate('orders.orderGroupsAddedToBundleSome', { count: newBundles.length }));
              }
              else {
                this.messageService.success(this.transloco.translate('orders.orderGroupsAddedToBundle', { count: newBundles.length }));
              }
            },
            error: _ => {
              this.messageService.error(this.transloco.translate('orders.failedToAddOrderGroupsTo'));
            }
          })
      })
  }
}
